// ViewingRequestForm.jsx



import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Modal,
  Paper,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { styles } from "./SingleListingPageStyles";

const ViewingRequestForm = ({
  weeklyTimes,
  specificDates,
  onSubmit,
  loadingSubmit,
  defaultName,
  defaultEmail,
}) => {
  const [name, setName] = useState(defaultName || "");
  const [email, setEmail] = useState(defaultEmail || "");
  const [phone, setPhone] = useState("");
  const [selectedDateId, setSelectedDateId] = useState("");
  const [weeklyDate, setWeeklyDate] = useState("");
  const [showDisclosures, setShowDisclosures] = useState(false);
  const [acceptedDisclosure, setAcceptedDisclosure] = useState(false);
  const [dateError, setDateError] = useState("");
  const dateInputRef = useRef(null);

  /* ---------------- props-to-state sync ---------------- */
  useEffect(() => setName(defaultName || ""), [defaultName]);
  useEffect(() => setEmail(defaultEmail || ""), [defaultEmail]);

  /* -------------- helpers / state logic --------------- */
  const selectedWeeklyTime = weeklyTimes.find((t) => t.id === selectedDateId);
  const isWeekly = !!selectedWeeklyTime;

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setWeeklyDate(newDate);

    if (isWeekly && newDate) {
      const dayOfWeek = new Date(newDate).toLocaleDateString("en-US", {
        weekday: "long",
      });
      setDateError(
        dayOfWeek === selectedWeeklyTime.day
          ? ""
          : `Please select a ${selectedWeeklyTime.day}`,
      );
    } else {
      setDateError(newDate ? "" : "Please select a date");
    }
  };

  const openCalendar = () => {
    if (dateInputRef.current?.showPicker) {
      // modern browsers
      dateInputRef.current.showPicker();
    } else {
      // fallback
      dateInputRef.current?.click();
    }
  };

  /* ---------------- disclosure text ------------------- */
  const disclosureText = `Renter disclosure for showings at showing.  
By scheduling this showing, you acknowledge that properties have cameras and audio monitoring and you consent to video monitoring or audio monitoring during your showing to help keep properties and personal belongings secure.   
Information shared with leasing agent may influence lease negotiations or approval decisions.  
If you are approved to lease properties shown by Tenant Access, cameras and audio recording by landlord or the property management company will not be permitted inside the home and those settings may be monitored by tenants with instructions that would be provided prior to move-in.  
By proceeding with showings on our platform, you (renters and accompanying guests) release liability and hold harmless property owner, property manager, property management company, Tenant Access and affiliates.`;

  /* ---------------- form submit flow ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowDisclosures(true);
  };

  const handleFinalSubmit = () => {
    if (loadingSubmit) return;
    if (!acceptedDisclosure) return alert("You must accept the disclosure.");
    setShowDisclosures(false);
    onSubmit({ name, email, phone, selectedDateId, weeklyDate });
  };

  const isSubmitDisabled =
    loadingSubmit || (isWeekly && (dateError || !weeklyDate));

  /* ===================== RENDER ====================== */

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
        <Typography variant="body1" sx={{ mb: 0.5 }}>
          Select an available time slot
        </Typography>

        {/* basic contact fields */}
        <input
          type="text"
          name="name"
          placeholder="Your Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={styles.inputField}
          required
        />
        <input
          type="email"
          name="email"
          placeholder="Your Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.inputField}
          required
        />
        <input
          type="tel"
          name="phone"
          placeholder="Your Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          style={styles.inputField}
          required
        />

        {/* slot selector */}
        <select
          name="date"
          value={selectedDateId}
          onChange={(e) => {
            setSelectedDateId(e.target.value);
            setWeeklyDate("");
            setDateError("");
          }}
          style={styles.inputField}
          required
        >
          <option value="">Select a time</option>
          {weeklyTimes.length > 0 && (
            <optgroup label="Weekly Availability">
              {weeklyTimes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.displayText}
                </option>
              ))}
            </optgroup>
          )}
          {specificDates.length > 0 && (
            <optgroup label="Specific Dates">
              {specificDates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.displayText}
                </option>
              ))}
            </optgroup>
          )}
        </select>

        {/* calendar icon + display */}
        {isWeekly && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mt: 1.5 }}>
              <IconButton aria-label="Open calendar" onClick={openCalendar}>
                <CalendarTodayIcon />
              </IconButton>

              {/* Invisible native date input (needed for picker) */}
              <input
                type="date"
                ref={dateInputRef}
                value={weeklyDate}
                onChange={handleDateChange}
                style={{
                  visibility: "hidden", // no placeholder ever visible
                  position: "absolute",
                  width: 0,
                  height: 0,
                  padding: 0,
                  margin: 0,
                  border: 0,
                }}
                required
                tabIndex={-1}
              />

              {weeklyDate ? (
                <Typography variant="body1">
                  {new Date(weeklyDate).toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: "#999" }}>
                  No date selected
                </Typography>
              )}
            </Box>

            {selectedWeeklyTime && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Please select a {selectedWeeklyTime.day}
              </Typography>
            )}
            {dateError && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                {dateError}
              </Typography>
            )}
          </>
        )}

        {/* submit button */}
        <Button
          variant="contained"
          sx={{
            mt: 1,
            backgroundColor: "#81c241",
            "&:hover": { backgroundColor: "#6aa832" },
          }}
          type="submit"
          disabled={isSubmitDisabled}
        >
          {loadingSubmit ? <CircularProgress size={24} /> : "Submit Request"}
        </Button>
      </Box>

      {/* Disclosure modal */}
      <Modal open={showDisclosures} onClose={() => setShowDisclosures(false)}>
        <Paper
          sx={{ p: 2, maxWidth: 600, mx: "auto", mt: "10%", textAlign: "center" }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Renter Disclosure
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            {disclosureText}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedDisclosure}
                onChange={(e) => setAcceptedDisclosure(e.target.checked)}
              />
            }
            label="I acknowledge and accept the terms."
          />
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#81c241",
                "&:hover": { backgroundColor: "#6aa832" },
              }}
              onClick={handleFinalSubmit}
              disabled={!acceptedDisclosure || loadingSubmit}
            >
              {loadingSubmit ? <CircularProgress size={24} /> : "Confirm & Submit"}
            </Button>
          </Box>
        </Paper>
      </Modal>
    </>
  );
};

export default ViewingRequestForm;







// import React, { useState } from "react";
// import { Box, Typography, Button, CircularProgress, Modal, Paper, FormControlLabel, Checkbox } from "@mui/material";
// import { styles } from "./SingleListingPageStyles";

// const ViewingRequestForm = ({ weeklyTimes, specificDates, onSubmit, loadingSubmit }) => {
//   const [name, setName] = useState("");
//   const [email, setEmail] = useState("");
//   const [phone, setPhone] = useState("");
//   const [selectedDateId, setSelectedDateId] = useState("");
//   const [weeklyDate, setWeeklyDate] = useState("");
//   const [showDisclosures, setShowDisclosures] = useState(false);
//   const [acceptedDisclosure, setAcceptedDisclosure] = useState(false);

//   const disclosureText = `Renter disclosure for showings at showing.  
//   By scheduling this showing, you acknowledge that properties have cameras and audio monitoring and you consent to video monitoring or audio monitoring during your showing to help keep properties and personal belongings secure.   
//   Information shared with leasing agent may influence lease negotiations or approval decisions.  
//   If you are approved to lease properties shown by Tenant Guided Tours, cameras and audio recording by landlord or the property management company will not be permitted inside the home and those settings may be monitored by tenants with instructions that would be provided prior to move-in.  
//   By proceeding with showings on our platform, you (renters and accompanying guests) release liability and hold harmless property owner, property manager, property management company, Tenant Guided Tours and affiliates.`;

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     setShowDisclosures(true);
//   };

//   const handleFinalSubmit = () => {
//     if (!acceptedDisclosure) {
//       alert("You must accept the disclosure before proceeding.");
//       return;
//     }
//     setShowDisclosures(false);
//     onSubmit({ name, email, phone, selectedDateId, weeklyDate });
//   };

//   const isWeekly = weeklyTimes.some((time) => time.id === selectedDateId);

//   return (
//     <>
//       <Box component="form" onSubmit={handleSubmit} sx={{ marginTop: "1rem" }}>
//         <Typography variant="body1" sx={{ marginBottom: "0.5rem" }}>Select an available time slot</Typography>
        
//         <input
//           type="text"
//           name="name"
//           placeholder="Your Name"
//           value={name}
//           onChange={(e) => setName(e.target.value)}
//           style={styles.inputField}
//           required
//         />
        
//         <input
//           type="email"
//           name="email"
//           placeholder="Your Email"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           style={styles.inputField}
//           required
//         />
        
//         <input
//           type="tel"
//           name="phone"
//           placeholder="Your Phone Number"
//           value={phone}
//           onChange={(e) => setPhone(e.target.value)}
//           style={styles.inputField}
//           required
//         />

//         <select
//           name="date"
//           value={selectedDateId}
//           onChange={(e) => setSelectedDateId(e.target.value)}
//           style={styles.inputField}
//           required
//         >
//           <option value="">Select a time</option>
//           {weeklyTimes.length > 0 && (
//             <optgroup label="Weekly Availability">
//               {weeklyTimes.map((time) => (
//                 <option key={time.id} value={time.id}>{time.displayText}</option>
//               ))}
//             </optgroup>
//           )}
//           {specificDates.length > 0 && (
//             <optgroup label="Specific Dates">
//               {specificDates.map((time) => (
//                 <option key={time.id} value={time.id}>{time.displayText}</option>
//               ))}
//             </optgroup>
//           )}
//         </select>

//         {isWeekly && (
//           <>
//             <input
//               type="date"
//               name="weeklyDate"
//               value={weeklyDate}
//               onChange={(e) => setWeeklyDate(e.target.value)}
//               style={styles.inputField}
//               required
//             />
//           </>
//         )}

//         <Button
//           variant="contained"
//           sx={{ marginTop: "1rem", backgroundColor: "#32CD32", "&:hover": { backgroundColor: "#28A428" } }}
//           type="submit"
//           disabled={loadingSubmit}
//         >
//           {loadingSubmit ? <CircularProgress size={24} /> : "Submit Request"}
//         </Button>
//       </Box>

//       {/* Disclosure Modal */}
//       <Modal open={showDisclosures} onClose={() => setShowDisclosures(false)}>
//         <Paper sx={{ padding: "2rem", maxWidth: "600px", margin: "auto", marginTop: "10%", textAlign: "center" }}>
//           <Typography variant="h6" sx={{ marginBottom: "1rem" }}>Renter Disclosure</Typography>
//           <Typography variant="body1" sx={{ marginBottom: "1rem", textAlign: "left" }}>
//             {disclosureText}
//           </Typography>
//           <FormControlLabel
//             control={<Checkbox checked={acceptedDisclosure} onChange={(e) => setAcceptedDisclosure(e.target.checked)} />}
//             label="I acknowledge and accept the terms."
//           />
//           <Box sx={{ marginTop: "1rem" }}>
//             <Button
//               variant="contained"
//               sx={{ backgroundColor: "#32CD32", "&:hover": { backgroundColor: "#28A428" } }}
//               onClick={handleFinalSubmit}
//               disabled={!acceptedDisclosure}
//             >
//               Confirm & Submit
//             </Button>
//           </Box>
//         </Paper>
//       </Modal>
//     </>
//   );
// };

// export default ViewingRequestForm;



// import React, { useState } from "react";
// import { Box, Typography, Button, CircularProgress } from "@mui/material";
// import { styles } from "./SingleListingPageStyles";

// const ViewingRequestForm = ({ weeklyTimes, specificDates, onSubmit, loadingSubmit }) => {
//   const [name, setName] = useState("");
//   const [email, setEmail] = useState("");
//   const [phone, setPhone] = useState("");
//   const [selectedDateId, setSelectedDateId] = useState("");
//   const [weeklyDate, setWeeklyDate] = useState("");

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     onSubmit({ name, email, phone, selectedDateId, weeklyDate });
//   };

//   const isWeekly = weeklyTimes.some((time) => time.id === selectedDateId);

//   return (
//     <Box component="form" onSubmit={handleSubmit} sx={{ marginTop: "1rem" }}>
//       <Typography variant="body1" sx={{ marginBottom: "0.5rem" }}>Select an available time slot</Typography>
//       <input
//         type="text"
//         name="name"
//         placeholder="Your Name"
//         value={name}
//         onChange={(e) => setName(e.target.value)}
//         style={styles.inputField}
//         required
//         autoFocus
//       />
//       <Typography variant="body2" color="textSecondary" sx={{ marginTop: "0.25rem" }}>Please enter your full name.</Typography>
//       <input
//         type="email"
//         name="email"
//         placeholder="Your Email"
//         value={email}
//         onChange={(e) => setEmail(e.target.value)}
//         style={styles.inputField}
//         required
//       />
//       <Typography variant="body2" color="textSecondary" sx={{ marginTop: "0.25rem" }}>We will send a confirmation to this email.</Typography>
//       <input
//         type="tel"
//         name="phone"
//         placeholder="Your Phone Number"
//         value={phone}
//         onChange={(e) => setPhone(e.target.value)}
//         style={styles.inputField}
//         required
//       />
//       <Typography variant="body2" color="textSecondary" sx={{ marginTop: "0.25rem" }}>Optional: Provide a phone number for faster communication.</Typography>
//       <select
//         name="date"
//         value={selectedDateId}
//         onChange={(e) => setSelectedDateId(e.target.value)}
//         style={styles.inputField}
//         required
//       >
//         <option value="">Select a time</option>
//         {weeklyTimes.length > 0 && (
//           <optgroup label="Weekly Availability">
//             {weeklyTimes.map((time) => (
//               <option key={time.id} value={time.id}>{time.displayText}</option>
//             ))}
//           </optgroup>
//         )}
//         {specificDates.length > 0 && (
//           <optgroup label="Specific Dates">
//             {specificDates.map((time) => (
//               <option key={time.id} value={time.id}>{time.displayText}</option>
//             ))}
//           </optgroup>
//         )}
//       </select>
//       {isWeekly && (
//         <>
//           <input
//             type="date"
//             name="weeklyDate"
//             value={weeklyDate}
//             onChange={(e) => setWeeklyDate(e.target.value)}
//             style={styles.inputField}
//             required
//           />
//           <Typography variant="body2" color="textSecondary" sx={{ marginTop: "0.25rem" }}>
//             Choose a specific date for the weekly availability.
//           </Typography>
//         </>
//       )}
//       <Button
//         variant="contained"
//         sx={{ marginTop: "1rem", backgroundColor: "#32CD32", "&:hover": { backgroundColor: "#28A428" } }}
//         type="submit"
//         disabled={loadingSubmit}
//       >
//         {loadingSubmit ? <CircularProgress size={24} /> : "Submit Request"}
//       </Button>
//     </Box>
//   );
// };

// export default ViewingRequestForm;